const OVERFLOW_REGEX = /(auto|scroll|hidden)/;
const OVERFLOW_REGEX_SCROLL = /(auto|scroll)/;

/** Given an element, find the closest parent element that is scrollable
 *
 * from https://stackoverflow.com/a/42543908
 */
export default function findScrollContainer(element: Element, includeHidden: boolean = false): Element {
  const style = getComputedStyle(element);
  const excludeStaticParent = style.position === 'absolute';
  if (style.position === 'fixed') {
    return document.body;
  }
  for (let parent: Element | null = element; parent; parent = parent.parentElement) {
    if (isScrollContainer(parent, {includeHidden, excludeStaticParent})) {
      return parent;
    }
  }
  return document.body;
}

/**
 * Determine if an element is visible on the screen based on its ancestor scroll containers' scroll positions.
 *
 * NOTE: this method uses `getBoundingClientRect()` which can trigger a reflow. If we're already in a context
 * where you are using getBoundingClientRect, this should not incur any significant additional cost.
 * It is conceivable that leveraging an IntersectionObserver could be used instead, but this is tricky
 * state to manage since it is parameterized on a single container where we would need one per parent
 * scroll container. It's recommended that for frequently firing events (e.g. scroll) that call this
 * function should be throttled.
 */
export function isInBoundsOfScrollContainers(element: Element): boolean {
  // Check if we are outside the bounds of any of our parent elements. We check them all since it's not
  // clear which scroll container is causing the element to be outside of the viewport.
  const rect = element.getBoundingClientRect();
  for (let parent: Element | null = element; parent; parent = parent.parentElement) {
    if (!isScrollContainer(parent)) {
      continue;
    }
    const parentRect = parent.getBoundingClientRect();
    if (
      rect.top < parentRect.top ||
      rect.bottom > parentRect.bottom ||
      rect.left < parentRect.left ||
      rect.right > parentRect.right
    ) {
      return false;
    }
  }
  return true;
}

function isScrollContainer(
  element: Element,
  {
    includeHidden = false,
    excludeStaticParent = false,
  }: {includeHidden?: boolean; excludeStaticParent?: boolean} = {}
): boolean {
  const style = getComputedStyle(element);
  const overflowRegex = includeHidden ? OVERFLOW_REGEX : OVERFLOW_REGEX_SCROLL;
  if (excludeStaticParent && style.position === 'static') {
    return false;
  }
  return overflowRegex.test(style.overflow + style.overflowY + style.overflowX);
}
